<script lang="ts" setup>
import { useLocalStorage } from '@vueuse/core';
import { type Connector, useAccount, useBalance } from '@wagmi/vue';

import { useUserStore } from '@/stores/user/useUserStore';
import { ScopeWagmiKeys } from '@/types';
import { useNuxtApp } from '#app';
import { onMounted, useWagmiConnectClient } from '#imports';
import useProviderConfig from '~/composables/web3/EVM/useProviderConfig';
import type { EvmConnectorId } from '~/service/EVM/config/types';

type Props = {
    isMobile?: boolean;
};

const props = defineProps<Props>();

const {
    '$wagmi': {
        'wallet': {
            isOpenWallet,
            toggleOpenWallet,
            setVisibleOpenWallet
        }
    }
} = useNuxtApp();

const userStore = useUserStore();

const {
    isConnected,
    isConnecting,
    address,
    isReconnecting,
    chainId
} = useAccount();

const {
    'data': balance,
    isLoading
} = useBalance({
    address,
    chainId,
    scopeKey: ScopeWagmiKeys.USE_BALANCE
});

type StepConnect = 'connect-wallet' | 'all-wallets';

const {
    connect,
    // connectWallet,
    connectors,
    isSuccess,
    isError,
    isPending
} = useWagmiConnectClient();
const { isDesktop } = useDevice();
const { connector: { config } } = useProviderConfig()(['connector']);

const privacyPolicy = useLocalStorage('privacyPolicy', false);
const showAlert = ref(false);
const isMounted = ref<boolean>(true);
const stepConnect = ref<StepConnect>('connect-wallet');

const recommendConnector = computed(() => connectors.value.find(connector => connector.id === config['zksync-sso'].id));
const otherConnectors = computed(() => connectors.value.filter(connector => connector.id !== config['zksync-sso'].id));

const handleConnect = (connector: Connector) => {
    if (!isDesktop) {
        if (connector.id === config.metaMaskSDK.id || connector.id === config.walletConnect.id) {
            // TODO в app.vue есть обработчик, который отслеживает возвращение юзера в приложение после коннекта или установки
            localStorage.setItem('connecting', 'true');
        }

        if (connector.id === config.coinbaseWalletSDK.id) {
            const dappUrl = import.meta.env.VITE_DOMEN_URL;
            const encodedUrl = encodeURIComponent(dappUrl);
            window.location.href = `https://go.cb-w.com/dapp?cb_url=${encodedUrl}`;
        }
    }

    connect({ connector });
};

const back = () => {
    stepConnect.value = stepConnect.value = 'connect-wallet';
};

const allWallets = () => {
    showAlert.value = !privacyPolicy.value;
    if (privacyPolicy.value) stepConnect.value = 'all-wallets';
};

onMounted(() => {
    isMounted.value = false;
});
</script>

<template>
  <Modal :model-value="isOpenWallet && !isConnected"
         :show-btn-back="stepConnect === 'all-wallets'"
         :width-content="400"
         class="display-flex justify-center align-items-center"
         @back-modal="back"
         @closed-modal="toggleOpenWallet">

    <div class="width-full display-flex flex-column justify-center align-items-center gap-20">

      <!-- шаг 1 -->
      <template v-if="stepConnect === 'connect-wallet'">
        <div class="display-flex flex-column justify-center align-items-center gap-8">
          <CrystalsMediaBox>
            <IconTypes :size="72"
                       name="placement/xsolla-zk"
            />
          </CrystalsMediaBox>
          <div class="width-full display-flex flex-column justify-center align-items-center gap-20">
            <p class="display-800-accent">Connect Wallet</p>

            <button v-if="recommendConnector"
                    class="xsolla-connect display-flex justify-center align-items-center gap-8 compact-500-accent"
                    type="button"
                    @click="handleConnect(recommendConnector)">
              <IconTypes :size="24"
                         name="placement/xsolla-zk" />
              Xsolla ZK Backpack
            </button>
          </div>
          <p v-if="showAlert"
             class="color-warning">Agree to the terms below</p>
        </div>

        <button class="display-flex justify-center align-items-center gap-8 compact-500-accent text-color-primary"
                type="button"
                @click="allWallets"
        >
          All wallets
          <IconTypes :size="24"
                     name="icon/arrow-r-l" />

        </button>

        <div class="width-full display-flex flex-row justify-start align-items-center gap-8">
          <Checkbox :checked="privacyPolicy"
                    label="Terms of Service & Privacy Policy"
                    @on-change="(value) => privacyPolicy = value " />
          <p class="compact-400-default text-color-primary">
            <span class="text-color-secondary"> {{ 'By proceeding, you agree to our ' }}</span>
            <NuxtLink @click="setVisibleOpenWallet(false)"
                      to="/docs/terms-of-use">Terms of Service</NuxtLink><span class="text-color-secondary"> & </span><NuxtLink @click="setVisibleOpenWallet(false)"
                                                                                                                                to="/docs/privacy-policy">Privacy Policy</NuxtLink></p>
        </div>
      </template>

      <!-- шаг 2 -->
      <template v-if="stepConnect === 'all-wallets'">
        <p class="compact-600-accent">
          All wallets
        </p>
        <div class="width-full display-flex flex-column gap-12">
          <p class="display-400-default text-color-secondary">
            Recommended wallet
          </p>
          <Web3ConnectorListItem v-if="recommendConnector"
                                 :id="recommendConnector.id as EvmConnectorId"
                                 :aria-label="`connect to ${recommendConnector.name}`"
                                 :is-error="isError"
                                 :is-loading="isPending"
                                 :is-success="isSuccess"
                                 :name="config[recommendConnector.id as EvmConnectorId]?.name || recommendConnector.name"
                                 @on-connect="handleConnect(recommendConnector)"
          />
        </div>
        <div class="width-full display-flex flex-column gap-12">
          <p class="display-400-default text-color-secondary">
            Others Wallet
          </p>
          <ul class="width-full display-flex flex-column gap-8">
            <li v-for="connector in otherConnectors"
                :key="connector.id">
              <Web3ConnectorListItem :id="connector.id as EvmConnectorId"
                                     :aria-label="`connect to ${connector.name}`"
                                     :is-error="isError"
                                     :is-loading="isPending"
                                     :is-success="isSuccess"
                                     :name="config[connector.id as EvmConnectorId]?.name || connector.name"
                                     @on-connect="handleConnect(connector)"
              />
            </li>
          </ul>
        </div>
      </template>
    </div>
  </Modal>
</template>

<style lang="scss" scoped>

.connect-wallet {
  position: relative;
  width: 348px;
  border-radius: var(--background-500-radius, 4px);
  background-color: var(--layer-floor-2);

  &-suspense {
    padding-top: 24px;
    padding-bottom: 24px;
  }

  @include mobile-576 {
    width: 100%;
    height: 100%;
  }
}

.slide-enter-active, .slide-leave-to {
  transform: scaleY(0);
}

.connect-modal-content {
  width: 300px;
}

.xsolla-connect {
  width: 100%;
  padding: 16px;
  border-radius: 2px;
  background: var(--bg-primary);
}

.color-warning {
  color: var(--content-negative)
}

</style>
